import React, { useState, useEffect } from 'react';
import { AppRouter } from './app/router';
import { Providers } from './app/providers/query-client';
import { LoginPage } from './features/auth/pages/LoginPage';
import { ResetPasswordPage } from './features/auth/pages/ResetPasswordPage';
import { PortalRouter } from './features/recrutamento-portal/PortalRouter';
import { AssinaturaPublicaPage } from './features/contratos/pages/AssinaturaPublicaPage';
import { authService, type User } from './shared/lib/auth';

/** O link do e-mail cai fora do AppRouter, que só existe para sessões ativas. */
const RESET_PASSWORD_PATH = '/reset-password';

/** Portal público de vagas — área externa, sem sessão. */
const PORTAL_VAGAS_PREFIX = '/vagas';

/** Link de assinatura de contrato enviado ao signatário externo — sem sessão. */
const ASSINATURA_PREFIX = '/assinatura/';

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(
    () => window.location.pathname === RESET_PASSWORD_PATH,
  );

  useEffect(() => {
    const initAuth = () => {
      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
        setShowLogin(false);
      } else {
        setShowLogin(true);
      }
      setIsLoading(false);
    };

    authService.addAuthListener((authUser) => {
      setUser(authUser);
      setShowLogin(!authUser);
    });

    initAuth();
  }, []);

  const handleLogin = async (email: string, password: string) => {
    try {
      await authService.login(email, password);
    } catch (error) {
      console.error('Erro no login:', error);
      alert('Usuário ou senha inválidos. Tente novamente.');
    }
  };

  const handleSignOut = () => {
    try {
      authService.logout();
      setUser(null);
      setShowLogin(true);
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  // O portal de vagas é anônimo por natureza: precede o gate de login
  // como `/reset-password`, e não toca no estado de sessão — quem já
  // está logado no app e abre /vagas continua logado ao voltar.
  if (window.location.pathname.startsWith(PORTAL_VAGAS_PREFIX)) {
    return <PortalRouter />;
  }

  // Signatário externo (sem conta no sistema) chega aqui pelo link único
  // enviado por e-mail — mesma lógica do portal de vagas: precede a sessão.
  if (window.location.pathname.startsWith(ASSINATURA_PREFIX)) {
    const token = window.location.pathname.slice(ASSINATURA_PREFIX.length);
    return <AssinaturaPublicaPage token={token} />;
  }

  // Redefinir senha precede qualquer sessão em cache: quem chega pelo link do
  // e-mail pode estar justamente recuperando uma conta comprometida.
  if (isResettingPassword) {
    const token = new URLSearchParams(window.location.search).get('token');
    return (
      <ResetPasswordPage
        token={token}
        onDone={() => {
          window.history.replaceState(null, '', '/');
          setIsResettingPassword(false);
          authService.logout();
          setUser(null);
          setShowLogin(true);
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user && showLogin) {
    return (
      <LoginPage
        onLogin={handleLogin}
        onPasswordChanged={async () => {}}
        forcePasswordChange={false}
        initialEmail=""
      />
    );
  }

  return (
    <Providers>
      <AppRouter user={user!} onSignOut={handleSignOut} />
    </Providers>
  );
}

export default App;
